import React, { useCallback, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

import { widthFromDragDelta } from './railWidths';

interface Props {
  side: 'left' | 'right';
  width: number;
  onChange: (next: number) => void;
  /** Optional commit callback invoked once on pointer-up — used to persist
   *  the final width without writing on every mousemove. */
  onCommit?: (final: number) => void;
}

/**
 * 6px-wide draggable handle for resizing one of the side rails. The
 * splitter sits *adjacent* to the rail it controls — on the rail's
 * inside edge — so dragging it visibly shifts the boundary between
 * rail + center.
 *
 * Behavior:
 *  - Pointer-down captures the current width as the drag anchor.
 *  - Pointer-move (window-level via setPointerCapture) computes the new
 *    width via `widthFromDragDelta`, clamps it, and emits via `onChange`.
 *  - Pointer-up releases capture and fires `onCommit` with the final
 *    width so the consumer can persist to localStorage.
 *
 * Cursor flips to `col-resize` while hovering. The handle is keyboard-
 * accessible (Left/Right + Home/End) for non-mouse users.
 */
export const RailSplitter: React.FC<Props> = ({ side, width, onChange, onCommit }) => {
  const startRef = useRef<{ x: number; w: number } | null>(null);
  const lastWidthRef = useRef(width);
  lastWidthRef.current = width;

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      startRef.current = { x: e.clientX, w: width };
      e.currentTarget.setPointerCapture(e.pointerId);
      // Prevent text-selection during the drag.
      document.body.style.userSelect = 'none';
    },
    [width],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const start = startRef.current;
      if (!start) return;
      const delta = e.clientX - start.x;
      const next = widthFromDragDelta(start.w, delta, side);
      lastWidthRef.current = next;
      onChange(next);
    },
    [side, onChange],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (startRef.current) {
        e.currentTarget.releasePointerCapture(e.pointerId);
        startRef.current = null;
        document.body.style.userSelect = '';
        onCommit?.(lastWidthRef.current);
      }
    },
    [onCommit],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const step = e.shiftKey ? 32 : 8;
      let direction = 0;
      if (e.key === 'ArrowLeft') direction = -step;
      else if (e.key === 'ArrowRight') direction = step;
      else if (e.key === 'Home') direction = -1024;
      else if (e.key === 'End') direction = 1024;
      else return;
      e.preventDefault();
      const next = widthFromDragDelta(width, direction, side);
      onChange(next);
      onCommit?.(next);
    },
    [side, width, onChange, onCommit],
  );

  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${side} panel`}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onKeyDown={handleKeyDown}
      sx={{
        width: 6,
        flexShrink: 0,
        cursor: 'col-resize',
        bgcolor: 'transparent',
        position: 'relative',
        // A subtle hover/focus indicator without competing visually with
        // the rail's content.
        '&:hover, &:focus-visible': { bgcolor: 'action.selected' },
        '&:focus-visible': { outline: 'none' },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 2,
          width: 2,
          bgcolor: theme => theme.palette.divider,
          opacity: 0.5,
        },
      }}
    />
  );
};
